import Box from 'UIKit/Box/Box'
import Flex from 'UIKit/Box/Flex'
import { BoxProps } from 'UIKit/Box/types'
import Text from 'UIKit/Text'
import { RowMiddle } from 'layout/Components/Row'
import { useState } from 'react'
import { Trans } from 'react-i18next'
import theme from 'theme'
import colors from 'theme/ui/colors'
import { limboGameReturnToPlayer, targetMultiplierMax, targetMultiplierMin } from 'views/InhouseGame/Limbo/config'
import { InHouseGameNumericalInput, InhouseGameInputContainer } from 'views/InhouseGame/components/styled'

const LimboGameTargetConfig: React.FC<
  BoxProps & { targetMultiplier: string; onTargetMultiplierChanged: (value: string) => void; disabled: boolean }
> = ({ targetMultiplier, onTargetMultiplierChanged, disabled, ...props }) => {
  const initialWinrate = limboGameReturnToPlayer / Number(targetMultiplier)
  const [winRate, setWinRate] = useState(
    Number.isNaN(initialWinrate) || !targetMultiplier || Number(targetMultiplier) === 0
      ? ''
      : initialWinrate.toString(),
  )
  const hasError = Number(targetMultiplier) < targetMultiplierMin || Number(targetMultiplier) > targetMultiplierMax
  return (
    <Flex
      {...props}
      p="24px"
      pb="14px"
      alignItems="center"
      justifyContent="center"
      width="100%"
      overflow="hidden"
      borderRadius={theme.radii.small}
      background={colors.backgroundAlt5}
    >
      <RowMiddle width="100%" justifyContent="space-between">
        <Box width={['calc(50% - 12px)']}>
          <Text fontSize="12px" bold color="textSubtle">
            <Trans>Target Multiplier</Trans>
          </Text>
          <InhouseGameInputContainer mt="6px" $error={hasError}>
            <RowMiddle width="100%">
              <InHouseGameNumericalInput
                aria-autocomplete="none"
                id="target-multiplier"
                autoFocus={false}
                disabled={disabled}
                tabIndex={1}
                onFocus={(e: any) => {
                  e.target.select()
                }}
                value={targetMultiplier}
                onBlur={() => {
                  const multiplier = Number(targetMultiplier)?.toFixed(2) || ''
                  onTargetMultiplierChanged(multiplier)
                  const winRate = limboGameReturnToPlayer / Number(multiplier)
                  setWinRate(Number.isNaN(winRate) || !multiplier || Number(multiplier) === 0 ? '' : winRate.toFixed(6))
                }}
                onValueChanged={(value) => {
                  const winRate = limboGameReturnToPlayer / Number(value)

                  setWinRate(Number.isNaN(winRate) || !value || Number(value) === 0 ? '' : winRate.toString())
                  onTargetMultiplierChanged(value)
                }}
              />
              <Box pl="4px" pr="16px" minWidth={16}>
                <Text fontSize="12px" bold color="textSubtle">
                  <Trans>x</Trans>
                </Text>
              </Box>
            </RowMiddle>
          </InhouseGameInputContainer>
          <Text fontSize="10px" height="10px" color="error" mt="4px" textAlign="right">
            {hasError &&
              (Number(targetMultiplier) < targetMultiplierMin ? (
                <Trans>Target multiplier must be greater than "{{ min: targetMultiplierMin }}x"</Trans>
              ) : (
                <Trans>Target multiplier must be less than "{{ max: targetMultiplierMax }}x"</Trans>
              ))}
          </Text>
        </Box>
        <Box width={['calc(50% - 12px)']}>
          <Text fontSize="12px" bold color="textSubtle">
            <Trans>Win chance</Trans>
          </Text>

          <InhouseGameInputContainer mt="6px" $error={hasError}>
            <RowMiddle width="100%">
              <InHouseGameNumericalInput
                aria-autocomplete="none"
                id="win-chance"
                autoFocus={false}
                tabIndex={1}
                disabled={disabled}
                value={winRate}
                onFocus={(e: any) => {
                  e.target.select()
                }}
                onBlur={() => {
                  const targetMultiplier = Number((limboGameReturnToPlayer / Number(winRate)).toFixed(2))
                  onTargetMultiplierChanged(
                    Number.isNaN(targetMultiplier) ||
                      !targetMultiplier ||
                      Number(targetMultiplier) === 0 ||
                      !winRate ||
                      Number(winRate) === 0
                      ? ''
                      : targetMultiplier.toString(),
                  )
                  const parsedWinRate = limboGameReturnToPlayer / targetMultiplier
                  setWinRate(
                    Number.isNaN(parsedWinRate) || !targetMultiplier || Number(targetMultiplier) === 0
                      ? ''
                      : parsedWinRate.toFixed(6),
                  )
                }}
                onValueChanged={(value) => {
                  const targetMultiplier = limboGameReturnToPlayer / Number(value)

                  onTargetMultiplierChanged(
                    Number.isNaN(targetMultiplier) || !value || Number(value) === 0 ? '' : targetMultiplier.toString(),
                  )
                  setWinRate(value)
                }}
              />
              <Box pl="4px" pr="16px" minWidth={16}>
                <Text fontSize="12px" bold color="textSubtle">
                  <Trans>%</Trans>
                </Text>
              </Box>
            </RowMiddle>
          </InhouseGameInputContainer>

          <Text fontSize="10px" height="10px" color="error" mt="4px" textAlign="right">
            {hasError &&
              (Number(targetMultiplier) < targetMultiplierMin ? (
                <Trans>
                  Win chance must be less than "{{ max: (limboGameReturnToPlayer / targetMultiplierMin).toFixed(6) }}%"
                </Trans>
              ) : (
                <Trans>
                  Win chance must be greater than "{{ min: (limboGameReturnToPlayer / targetMultiplierMax).toFixed(6) }}
                  %"
                </Trans>
              ))}
          </Text>
        </Box>
      </RowMiddle>
    </Flex>
  )
}

export default LimboGameTargetConfig
